'use client';

import * as React from 'react';
import type { LucideIcon } from 'lucide-react';

import {
  SEGMENTED_TOGGLE_SIZE_STYLES,
  SEGMENTED_TOGGLE_TONE_STYLES,
  type SegmentedToggleSize,
  type SegmentedToggleTone,
} from '@/lib/design-system/segmented-toggle';
import { cn } from '@/lib/utils';

export type SegmentedToggleOption<T extends string> = {
  value: T;
  label: React.ReactNode;
  tooltip?: string;
  icon?: LucideIcon;
  count?: number;
  disabled?: boolean;
};

export type SegmentedToggleProps<T extends string> = {
  value: T;
  onChange: (value: T) => void;
  options: readonly SegmentedToggleOption<T>[];
  size?: SegmentedToggleSize;
  tone?: SegmentedToggleTone;
  fullWidth?: boolean;
  iconOnly?: boolean;
  className?: string;
  ariaLabel?: string;
};

export function SegmentedToggle<T extends string>({
  value,
  onChange,
  options,
  size = 'md',
  tone = 'default',
  fullWidth = false,
  iconOnly = false,
  className,
  ariaLabel,
}: SegmentedToggleProps<T>) {
  const sizing = SEGMENTED_TOGGLE_SIZE_STYLES[size];
  const toneStyles = SEGMENTED_TOGGLE_TONE_STYLES[tone];

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-stretch',
        sizing.shell,
        toneStyles.shell,
        fullWidth && 'w-full',
        className,
      )}
    >
      {options.map((option) => {
        const isActive = option.value === value;
        const Icon = option.icon;
        const tooltipText = option.tooltip ?? (typeof option.label === 'string' ? option.label : undefined);
        return (
          <button
            key={option.value}
            role="tab"
            type="button"
            aria-selected={isActive}
            aria-label={iconOnly ? tooltipText : undefined}
            title={iconOnly ? tooltipText : undefined}
            disabled={option.disabled}
            onClick={() => !option.disabled && onChange(option.value)}
            className={cn(
              'inline-flex cursor-pointer items-center justify-center rounded-[2px] transition-[background-color,color,box-shadow]',
              // Content-sized toggles keep one line; full-width toggles let long
              // labels wrap (and shrink) instead of clipping on narrow screens.
              fullWidth ? 'min-w-0 flex-1 text-center' : 'whitespace-nowrap',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-0',
              'disabled:pointer-events-none disabled:opacity-50',
              iconOnly ? sizing.iconOnlyButton : sizing.button,
              isActive ? toneStyles.active : toneStyles.inactive,
            )}
          >
            {Icon ? <Icon className={sizing.icon} aria-hidden="true" /> : null}
            {!iconOnly && (
              <span className={cn(sizing.label, fullWidth && 'leading-tight')}>{option.label}</span>
            )}
            {!iconOnly && typeof option.count === 'number' && (
              <span
                className={cn(
                  sizing.count,
                  isActive ? toneStyles.countActive : toneStyles.countInactive,
                )}
              >
                {option.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
